import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "prompt": "A serene beach at sunset with gentle waves rolling onto the shore",
 *   "size": "1280x720",
 *   "seconds": 4
 * }
 * ```
 */
export interface MicrosoftSora2Params {
  /**
   * 视频生成提示词，描述要生成的视频内容，长度必须在1-1000字符之间
   */
  prompt: string;
  /**
   * 视频尺寸，格式为'宽x高'。支持分辨率包括: '720x1280', '1280x720', '1024x1792', and '1792x1024
   */
  size?: string;
  /**
   * 视频时长（秒），支持的值为: 4, 8, 12
   * @values 4 | 8 | 12
   */
  seconds?: 4 | 8 | 12;
  /**
   * 输入参考图像URL，用于图像到视频(I2V)生成。如提供，系统会基于此图像生成视频
   */
  input_reference?: string;
}

export interface MicrosoftSora2Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * microsoft_sora2
 *
 * <p>API Documentation: https://apim-onc5zkda5mp7w.azure-api.net</p>
 * <p>Microsoft Sora2 视频生成实现。支持文本到视频和图像到视频的生成。</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, microsoftSora2 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await microsoftSora2(
 *   {
 *     "prompt": "A serene beach at sunset with gentle waves rolling onto the shore",
 *     "size": "1280x720",
 *     "seconds": 4
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 MicrosoftSora2Params 接口定义
 * @returns 生成的资源列表
 */
export async function microsoftSora2(
  params: MicrosoftSora2Params,
): Promise<MicrosoftSora2Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'microsoft_sora2',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: MicrosoftSora2Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
